/* eslint-disable no-restricted-imports */
import React, { forwardRef, HTMLAttributes } from 'react';
import {
  ChipProps as MuiChipProps,
  Chip as MUIChip,
  Stack,
  Box,
} from '@mui/material';
import cn from 'classnames';

import { Corner } from '../..';
import Badge from '../Badge';
import { getChipBadgeVariant, getChipVariant, hasContent } from './utils';
import { ChipLabel } from './components/ChipLabel';

export interface BaseChipProps extends MuiChipProps {
  testID?: string;
  active?: boolean;
  endIcon?: React.ReactElement;
  corner?: Corner;
  bold?: boolean;
  title?: string;
  stackedLabel?: never;
  badgeContent?: React.ReactNode;
  tooltipContent?: React.ReactNode;
}

export interface StackedLabelChipProps
  extends Omit<BaseChipProps, 'stackedLabel' | 'title' | 'label'> {
  title: string;
  stackedLabel: boolean;
  label: React.ReactNode;
}

export type ChipProps = StackedLabelChipProps | BaseChipProps;

const Chip = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & ChipProps
>(
  (
    {
      testID,
      label,
      active,
      icon,
      title,
      endIcon,
      variant,
      stackedLabel,
      badgeContent,
      tooltipContent,
      corner = 'round',
      bold = false,
      ...props
    }: ChipProps,
    ref
  ) => {
    const hasDeleteIcon = !!props.onDelete;
    const hasBadge = hasContent(badgeContent);

    const getEndContent = () => {
      if (!hasBadge && !endIcon) return null;
      return (
        <Stack direction='row' alignItems='center' spacing='0.5'>
          {hasBadge && (
            <Box className='MuiChip-icon MuiChip-endIcon'>
              <Badge
                badgeContent={badgeContent}
                corner={corner}
                variant={getChipBadgeVariant(variant, active)}
                data-test-id={`${testID || 'chip'}-badge`}
                color={variant === 'filled' ? 'inverse' : props.color}
                size={props.size}
                showZero
                max={99999}
              />
            </Box>
          )}
          {endIcon && React.cloneElement(endIcon, {
            className: 'MuiChip-icon MuiChip-endIcon',
          })}
        </Stack>
      );
    };

    const endContent = getEndContent();

    return (
      <MUIChip
        ref={ref}
        data-test-id={testID}
        icon={icon}
        classes={{
          label: cn(
            !!icon && 'withStartIcon',
            !!endContent && 'withEndIcon',
            hasDeleteIcon && 'withDeleteIcon',
            { bold }
          ),
        }}
        aria-pressed={active}
        label={
          <ChipLabel
            label={label}
            tooltipContent={tooltipContent}
            endContent={endContent}
            title={title}
          />
        }
        {...props}
        variant={getChipVariant(variant, active)}
        className={cn(
          `MuiChip-corner-${corner}`,
          active && 'MuiChip-active',
          stackedLabel && 'MuiChip-stacked',
          props.className
        )}
      />
    );
  }
);

export default Chip;
